import React, { useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { Form } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { GenerateTemplatePageState } from './GenerateTemplate'
import { TEMPLATE_TYPES } from './TemplatesTab'
import { TemplateTabContent } from '../../../models/TemplateLibrary'
import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import './Templates.scss'

const CREATABLE_TEMPLATE_TYPES = TEMPLATE_TYPES.filter((t) => t.key !== 'your-templates')

const DEFAULT_TYPE = CREATABLE_TEMPLATE_TYPES.find((t) => t.key === 'meeting-summary') ?? CREATABLE_TEMPLATE_TYPES[0]

const findMatchingType = (templateType: string, templateTypeId: string): TemplateTabContent | undefined =>
  CREATABLE_TEMPLATE_TYPES.find((t) => t.searchParams.type === templateType && (t.searchParams.typeId ?? t.key) === templateTypeId)

export const TemplateCreationPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { portalRef } = useContext(HeaderContext)

  const { hasScope } = useZocksAuth()
  const hasTemplateAdminScope = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin'])

  const incomingState = (location.state as GenerateTemplatePageState) ?? {}
  const initialType = findMatchingType(incomingState.templateType ?? '', incomingState.templateTypeId ?? '') ?? DEFAULT_TYPE

  const [selectedTypeKey, setSelectedTypeKey] = useState(initialType.key)
  const [name, setName] = useState(`New ${initialType.label} template`)
  const [description, setDescription] = useState('')
  const [scope, setScope] = useState<'account' | 'user'>(hasTemplateAdminScope ? 'account' : 'user')

  const selectedType = CREATABLE_TEMPLATE_TYPES.find((t) => t.key === selectedTypeKey) ?? DEFAULT_TYPE

  const handleTypeChange = (key: string) => {
    const type = CREATABLE_TEMPLATE_TYPES.find((t) => t.key === key)
    if (!type) return
    setSelectedTypeKey(key)
    setName(`New ${type.label} template`)
  }

  const handleCreateTemplate = () => {
    const state: GenerateTemplatePageState = {
      templateTypeName: selectedType.label,
      templateType: selectedType.searchParams.type ?? '',
      templateTypeId: selectedType.searchParams.typeId ?? selectedType.key,
      templateName: name.trim(),
      templateDescription: description.trim() || undefined,
      templateScope: scope,
    }
    navigate('/workspace/setup/templates/template/create/generate', { state, replace: true })
  }

  const typeOptions: SelectOption[] = CREATABLE_TEMPLATE_TYPES.map((t) => ({ id: t.key, label: t.label }))
  const selectedTypeOption = typeOptions.find((o) => o.id === selectedTypeKey) ?? typeOptions[0]

  const handleTypeOptionSelected = (option: SelectOption) => {
    handleTypeChange(option.id)
  }

  return (
    <>
      {portalRef && portalRef.current
        ? createPortal(
            <div className='d-flex align-items-center zocks-pl-2.0'>
              <Button variant='link' className='back-button' onClick={() => navigate(-1)}>
                <i className='bi bi-chevron-left' />
              </Button>
            </div>,
            portalRef.current,
          )
        : null}

      <div className='d-flex flex-column align-items-center overflow-y-auto w-100 pt-5'>
        <div className='d-flex flex-column gap-4 w-100 choose-template-creation-form'>
          <h5 className='fw-bold text-center mb-0'>Create new template</h5>
          <div className='d-flex flex-column gap-4'>
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <SelectDropdown
                options={typeOptions}
                initialSelectedOption={selectedTypeOption}
                onOptionSelected={handleTypeOptionSelected}
                className='choose-template-type-select'
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control type='text' value={name} onChange={(e) => setName(e.target.value)} />
            </Form.Group>

            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as='textarea'
                rows={3}
                placeholder='Briefly describe what this template is for'
                value={description}
                className={'template-description-textarea'}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Access</Form.Label>
              {hasTemplateAdminScope ? (
                <div className='d-flex flex-column gap-2'>
                  <Form.Check
                    type='radio'
                    id='scope-account'
                    name='scope'
                    checked={scope === 'account'}
                    onChange={() => setScope('account')}
                    label={
                      <span>
                        <i className='bi bi-people-fill me-1' />
                        Account <span className='zocks-text-gray-600'>(anyone in your account)</span>
                      </span>
                    }
                  />
                  <Form.Check
                    type='radio'
                    id='scope-user'
                    name='scope'
                    checked={scope === 'user'}
                    onChange={() => setScope('user')}
                    label={
                      <span>
                        <i className='bi bi-person-fill me-1' />
                        Personal <span className='zocks-text-gray-600'>(only you)</span>
                      </span>
                    }
                  />
                </div>
              ) : (
                <div className='d-flex gap-2 align-items-center'>
                  <i className='bi bi-person-fill flex-shrink-0' />
                  <span>
                    Personal <span className='zocks-text-gray-600'>(only you)</span>
                  </span>
                </div>
              )}
            </Form.Group>
          </div>

          <div className='d-flex gap-2 justify-content-end'>
            <Button variant='secondary' onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleCreateTemplate} disabled={!name.trim()}>
              Create template
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
